/**
 * FooterConfigPanel Component for UI-Model-1
 *
 * Full footer configuration form using Design System components.
 * Integrates with the command pattern for undo/redo support.
 * Mirrors HeaderConfigPanel structure.
 */

import React, { useCallback, useRef } from 'react';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import TextField from 'Components/DesignSystem/TextField/TextField';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { useSaveQueue } from '../../contexts/SaveQueueContext';
import { useDebouncedInput } from '../../hooks/useDebouncedInput';
import { createUpdateFooterCommand } from '../../types/command.types';
import { FooterConfigurationAPI, DEFAULT_FOOTER_CONFIGURATION } from '../../types/api.types';
import { SaveDataByType } from '../../services/SaveQueue';
import { IconUploadSection } from './shared/IconUploadSection';
import { ButtonConfigSection } from './shared/ButtonConfigSection';

interface FooterConfigPanelProps {
  id?: string;
}

export const FooterConfigPanel = ({ id }: FooterConfigPanelProps) => {
  const baseId = id || 'footer-config-panel';
  const { state, dispatch } = useUIModelState();
  const { enqueueSave } = useSaveQueue();
  const footerConfig = state.model?.layout.footer?.configuration ?? { ...DEFAULT_FOOTER_CONFIGURATION };

  // Refs for file uploads (to track pending files for save)
  const previousButtonIconFileRef = useRef<File | undefined>(undefined);
  const nextButtonIconFileRef = useRef<File | undefined>(undefined);
  const doneButtonIconFileRef = useRef<File | undefined>(undefined);

  // Helper to dispatch footer updates
  const updateFooter = useCallback(
    (updates: Partial<FooterConfigurationAPI>, description?: string) => {
      const command = createUpdateFooterCommand(
        updates,
        description || 'Update footer configuration'
      );
      dispatch({ type: 'EXECUTE_COMMAND', payload: command });
      dispatch({ type: 'MARK_DIRTY', payload: { section: 'footer' } });

      // Merge updates with current footer to avoid stale closure issue
      const updatedFooter = {
        ...footerConfig,
        ...updates,
      };
      const saveData: SaveDataByType['footer'] = {
        footerData: updatedFooter,
        previousButtonIconFile: previousButtonIconFileRef.current,
        nextButtonIconFile: nextButtonIconFileRef.current,
        doneButtonIconFile: doneButtonIconFileRef.current,
      };
      enqueueSave('footer', saveData);
    },
    [dispatch, footerConfig, enqueueSave]
  );

  // Debounced input for description text
  const descriptionTextInput = useDebouncedInput(
    footerConfig.descriptionText || '',
    (value) => updateFooter({ descriptionText: value }, 'Update footer description'),
    2000
  );

  return (
    <div id={baseId} data-testid={baseId} className="tw-space-y-6">
      {/* Show Footer Toggle - at top of panel */}
      <div className="tw-flex tw-items-center tw-justify-between tw-pb-4 tw-border-b tw-border-edge">
        <span className="tw-text-title tw-text-ink">Footer</span>
        <Toggle
          label=""
          checked={footerConfig.showFooter}
          onChange={(checked) =>
            updateFooter({ showFooter: checked }, `${checked ? 'Show' : 'Hide'} footer`)
          }
        />
      </div>

      {/* Section: Description */}
      {footerConfig.showFooter && (
        <div className="tw-space-y-4">
          <Checkbox
            label="Show Description"
            checked={footerConfig.showDescription ?? false}
            onChange={(checked) =>
              updateFooter({ showDescription: checked }, `${checked ? 'Show' : 'Hide'} footer description`)
            }
          />

          {footerConfig.showDescription && (
            <div className="tw-space-y-3 tw-pl-4 tw-border-l-2 tw-border-edge">
              <TextField
                label="Description Text"
                labelSize="sm"
                value={descriptionTextInput.localValue}
                onChange={descriptionTextInput.handleChange}
                placeholder="Enter footer description"
              />
            </div>
          )}
        </div>
      )}

      {/* Section: Previous Button */}
      {footerConfig.showFooter && (
        <div className="tw-space-y-4">
          <FieldTitle label="Previous Button" size="sm" />
          <ButtonConfigSection
            buttonName="Previous"
            showButton={footerConfig.showPreviousButton ?? false}
            buttonText={footerConfig.previousButtonText ?? 'Previous'}
            showIcon={footerConfig.previousButtonShowIcon ?? false}
            iconUrl={footerConfig.previousButtonIconUrl}
            showTooltip={footerConfig.previousButtonShowTooltip ?? false}
            tooltipText={footerConfig.previousButtonTooltipText}
            onShowButtonChange={(show) =>
              updateFooter({ showPreviousButton: show }, `${show ? 'Show' : 'Hide'} previous button`)
            }
            onTextChange={(text) =>
              updateFooter({ previousButtonText: text }, 'Update previous button text')
            }
            onShowIconChange={(show) =>
              updateFooter({ previousButtonShowIcon: show }, `${show ? 'Show' : 'Hide'} previous button icon`)
            }
            onIconUrlChange={(url) => {
              if (!url) previousButtonIconFileRef.current = undefined;
              updateFooter({ previousButtonIconUrl: url }, 'Update previous button icon');
            }}
            onIconFileChange={(file) => {
              previousButtonIconFileRef.current = file;
            }}
            onShowTooltipChange={(show) =>
              updateFooter({ previousButtonShowTooltip: show }, `${show ? 'Show' : 'Hide'} previous button tooltip`)
            }
            onTooltipTextChange={(text) =>
              updateFooter({ previousButtonTooltipText: text }, 'Update previous button tooltip')
            }
            baseId={baseId}
          />
        </div>
      )}

      {/* Section: Next Button */}
      {footerConfig.showFooter && (
        <div className="tw-space-y-4">
          <FieldTitle label="Next Button" size="sm" />
          <ButtonConfigSection
            buttonName="Next"
            showButton={footerConfig.showNextButton ?? false}
            buttonText={footerConfig.nextButtonText ?? 'Next'}
            showIcon={footerConfig.nextButtonShowIcon ?? false}
            iconUrl={footerConfig.nextButtonIconUrl}
            showTooltip={footerConfig.nextButtonShowTooltip ?? false}
            tooltipText={footerConfig.nextButtonTooltipText}
            onShowButtonChange={(show) =>
              updateFooter({ showNextButton: show }, `${show ? 'Show' : 'Hide'} next button`)
            }
            onTextChange={(text) =>
              updateFooter({ nextButtonText: text }, 'Update next button text')
            }
            onShowIconChange={(show) =>
              updateFooter({ nextButtonShowIcon: show }, `${show ? 'Show' : 'Hide'} next button icon`)
            }
            onIconUrlChange={(url) => {
              if (!url) nextButtonIconFileRef.current = undefined;
              updateFooter({ nextButtonIconUrl: url }, 'Update next button icon');
            }}
            onIconFileChange={(file) => {
              nextButtonIconFileRef.current = file;
            }}
            onShowTooltipChange={(show) =>
              updateFooter({ nextButtonShowTooltip: show }, `${show ? 'Show' : 'Hide'} next button tooltip`)
            }
            onTooltipTextChange={(text) =>
              updateFooter({ nextButtonTooltipText: text }, 'Update next button tooltip')
            }
            baseId={baseId}
          />
        </div>
      )}

      {/* Section: Done Button */}
      {footerConfig.showFooter && (
        <div className="tw-space-y-4">
          <FieldTitle label="Done Button" size="sm" />
          <ButtonConfigSection
            buttonName="Done"
            showButton={footerConfig.showDone ?? false}
            buttonText={footerConfig.doneButtonText ?? 'Done'}
            showIcon={footerConfig.doneButtonShowIcon ?? false}
            iconUrl={footerConfig.doneButtonIconUrl}
            showTooltip={footerConfig.doneButtonShowTooltip ?? false}
            tooltipText={footerConfig.doneButtonTooltipText}
            onShowButtonChange={(show) =>
              updateFooter({ showDone: show }, `${show ? 'Show' : 'Hide'} done button`)
            }
            onTextChange={(text) =>
              updateFooter({ doneButtonText: text }, 'Update done button text')
            }
            onShowIconChange={(show) =>
              updateFooter({ doneButtonShowIcon: show }, `${show ? 'Show' : 'Hide'} done button icon`)
            }
            onIconUrlChange={(url) => {
              if (!url) doneButtonIconFileRef.current = undefined;
              updateFooter({ doneButtonIconUrl: url }, 'Update done button icon');
            }}
            onIconFileChange={(file) => {
              doneButtonIconFileRef.current = file;
            }}
            onShowTooltipChange={(show) =>
              updateFooter({ doneButtonShowTooltip: show }, `${show ? 'Show' : 'Hide'} done button tooltip`)
            }
            onTooltipTextChange={(text) =>
              updateFooter({ doneButtonTooltipText: text }, 'Update done button tooltip')
            }
            baseId={baseId}
          />
        </div>
      )}
    </div>
  );
};

export default FooterConfigPanel;
